import { useState } from "react";
import type { TimelineCanary, TimelineResponse } from "../api/types";

// One canary at a time, on a single horizontal line: each point is the canary's
// current version arriving at a dependent env, at its projected deploy time.

const DAY_MS = 86_400_000;

function fmtDateTime(iso: string): string {
    return new Date(iso).toLocaleString(undefined, {
        weekday: "short",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
}

function fmtDay(d: Date): string {
    return d.toLocaleDateString(undefined, { weekday: "short", day: "numeric" });
}

export default function PromotionTimeline({ timeline }: { timeline: TimelineResponse }) {
    const canaries = timeline.canaries;
    const [head, setHead] = useState<string | null>(canaries[0]?.head ?? null);
    const canary = canaries.find((c) => c.head === head) ?? canaries[0] ?? null;

    if (!canary) return <p className="muted">No canaries found in the promotion config.</p>;

    return (
        <div className="timeline">
            {canaries.length > 1 ? (
                <div className="tl-picker">
                    {canaries.map((c) => (
                        <button
                            key={c.head}
                            className={`btn btn-small ${c.head === canary.head ? "active" : ""}`}
                            onClick={() => setHead(c.head)}
                        >
                            {c.head}
                        </button>
                    ))}
                </div>
            ) : null}
            <CanaryLine canary={canary} generatedAt={timeline.generatedAt} />
        </div>
    );
}

function CanaryLine({ canary, generatedAt }: { canary: TimelineCanary; generatedAt: string }) {
    const now = Date.parse(generatedAt);
    const times = canary.events.map((e) => Date.parse(e.at));
    const lastMs = times.length ? Math.max(...times) : now + 7 * DAY_MS;
    const span = Math.max(lastMs - now, DAY_MS) * 1.1; // pad so the final point isn't at the edge
    const pct = (ms: number): number => Math.max(0, Math.min(100, ((ms - now) / span) * 100));

    const dayCount = Math.ceil(span / DAY_MS);
    const step = Math.max(1, Math.ceil(dayCount / 8));
    const dayMarks: { at: number; label: string }[] = [];
    for (let d = step; d <= dayCount; d += step) dayMarks.push({ at: d, label: fmtDay(new Date(now + d * DAY_MS)) });

    return (
        <section className="tl-canary">
            <header className="tl-canary-head">
                <span className="cloud-badge">{canary.category}</span>
                <strong>{canary.head}</strong>
                <span className="muted">canary</span>
                <span className="tl-canary-version">
                    {canary.version ? `version ${canary.version}` : "version unknown"}
                    {canary.versionLandedAt ? <span className="muted"> · landed {fmtDateTime(canary.versionLandedAt)}</span> : null}
                </span>
            </header>

            {canary.events.length === 0 ? (
                <p className="muted">
                    No downstream deployments projected — every dependent env is already on {canary.version ?? "this version"}.
                </p>
            ) : (
                <div className="tl-line">
                    {dayMarks.map((m) => (
                        <span key={m.at} className="tl-grid" style={{ left: `${pct(now + m.at * DAY_MS)}%` }} />
                    ))}
                    <div className="tl-axis" />
                    <span className="tl-now-dot" style={{ left: "0%" }} title={`now · ${fmtDateTime(generatedAt)}`} />
                    <span className="tl-now-cap">now</span>

                    {canary.events.map((e, i) => (
                        <div
                            key={e.envKey}
                            className={`tl-point ${i % 2 === 0 ? "above" : "below"}`}
                            style={{ left: `${pct(Date.parse(e.at))}%` }}
                        >
                            <span className="tl-stem" />
                            <span className="tl-dot" />
                            <span
                                className="tl-callout"
                                title={`${e.envKey}: ${e.fromVersion ?? "?"} ⇒ ${e.version} · ${fmtDateTime(e.at)} · soak ${e.soakDays}d · ${e.mergePolicy}`}
                            >
                                <span className="tl-callout-env">{e.envKey}</span>
                                <span className="tl-callout-ver">
                                    {e.fromVersion ? (
                                        <>
                                            <span className="muted">{e.fromVersion}</span> ⇒ {e.version}
                                        </>
                                    ) : (
                                        e.version
                                    )}
                                </span>
                                <span className="tl-callout-when muted">{fmtDateTime(e.at)}</span>
                            </span>
                        </div>
                    ))}

                    {dayMarks.map((m) => (
                        <span key={`l-${m.at}`} className="tl-day-label" style={{ left: `${pct(now + m.at * DAY_MS)}%` }}>
                            {m.label}
                        </span>
                    ))}
                </div>
            )}

            {(canary.upToDate.length > 0 || canary.unknown.length > 0) && (
                <div className="tl-notes muted">
                    {canary.upToDate.length > 0 ? <div>Up to date: {canary.upToDate.join(", ")}</div> : null}
                    {canary.unknown.length > 0 ? (
                        <div>Unprojected: {canary.unknown.map((u) => `${u.envKey} (${u.reason})`).join("; ")}</div>
                    ) : null}
                </div>
            )}
        </section>
    );
}
